import React, { useState, useEffect, useRef } from 'react';
import './DataTable.css'; // Custom CSS file for additional styling
import { Button, Dropdown, DropdownMenu, DropdownToggle, DropdownItem } from 'reactstrap';
import { useLocation } from 'react-router-dom';
import ReactDOM from 'react-dom';
import apiRequest from '../../api';

const checkTextOverflow = (text, container) => {
  if (!container) return false;

  // Get the computed style of the actual cell
  const computedStyle = window.getComputedStyle(container);

  // Create a temporary span element
  const span = document.createElement('span');
  span.style.visibility = 'hidden';
  span.style.position = 'absolute';
  span.style.whiteSpace = 'nowrap';
  // Copy the computed font styles from the cell
  span.style.fontSize = computedStyle.fontSize;
  span.style.fontFamily = computedStyle.fontFamily;
  span.style.fontWeight = computedStyle.fontWeight;
  span.style.letterSpacing = computedStyle.letterSpacing;
  span.innerText = text;
  document.body.appendChild(span);

  // Get the width of the text and container
  const textWidth = span.offsetWidth;
  const containerWidth = container.offsetWidth;

  // Clean up
  document.body.removeChild(span);

  // Return true if text width is greater than container width
  return textWidth > containerWidth;
};

const DataTable = ({
  data,
  columns,
  visibleRows = data.length,
  onEdit = () => { },
  fetchReservations,
  selectedUsers = new Set(),
  onUserSelect = () => { },
  ...rest
}) => {

  const selectedLanguageRef = useRef(localStorage.getItem('Language') || 'EN');
  useEffect(() => {
    // Handle changes from other tabs/windows and local changes
    const handleLanguageChange = (event) => {
      selectedLanguageRef.current = event.detail['selectedLanguage'];
      
    };

    window.addEventListener('languageChange', handleLanguageChange);
    return () => {
      window.removeEventListener('languageChange', handleLanguageChange);
    };
  }, []);

  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'ascending' });
  const [sortedData, setSortedData] = useState([...data]);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, itemId: null });
  const [longPressTimer, setLongPressTimer] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  const { pathname } = useLocation()
  const mainBodyRef = useRef(null);
  const tableRef = useRef(null);
  const cellRefs = useRef({});

  // Detect if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.matchMedia('(max-width: 768px), (pointer: coarse)').matches);
    };

    checkMobile(); // Check on initial load
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    // Update sortedData whenever data (filteredData) changes
    console.log("DataTable data changed:", data);
    setSortedData([...data]);
  }, [data]);

  const sortData = (column) => {
    let direction = 'ascending';
    if (sortConfig.key === column && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }

    let sortedArray;

    if (column === "username") {
      sortedArray = [...data].sort((a, b) => {
        return direction === "ascending"
          ? a.username.toLowerCase().localeCompare(b.username.toLowerCase())
          : b.username.toLowerCase().localeCompare(a.username.toLowerCase());
      });
    } else {
      sortedArray = [...data].sort((a, b) => {
        if (a[column] < b[column]) {
          return direction === 'ascending' ? -1 : 1;
        }
        if (a[column] > b[column]) {
          return direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }

    setSortConfig({ key: column, direction });
    setSortedData(sortedArray);
  };

  // Function to handle traditional right-click context menu
  const handleContextMenu = (item, event) => {
    event.preventDefault(); // Prevent default context menu
    event.stopPropagation();
    if (selectedUsers.size > 0) {
      return;
    }

    showContextMenuAt(item, event.clientX, event.clientY);
  };

  // Function to handle long-press (for mobile)
  const handleTouchStart = (item, event) => {
    if (selectedUsers.size > 0) {
      return;
    }

    // Start a timer for long press
    const timer = setTimeout(() => {
      // Get touch position
      const touch = event.touches[0];

      // Show context menu at touch position
      showContextMenuAt(item, touch.clientX, touch.clientY);

      // Provide haptic feedback if available
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
    }, 500); // 500ms long press

    setLongPressTimer(timer);
  };

  const handleTouchEnd = () => {
    // Clear the timer if touch ends before long press is triggered
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  const handleTouchMove = () => {
    // Clear the timer if user moves finger during press
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  // Helper function to show context menu at specified coordinates
  const showContextMenuAt = (item, x, y) => {
    // Get the bounding rectangle of the table
    const tableRect = tableRef.current?.getBoundingClientRect();

    if (!tableRect) return;

    // Calculate the horizontal center if within the table
    const centerX = tableRect.left + tableRect.width / 2;

    setContextMenu({
      visible: true,
      x: centerX,
      y: y,
      itemId: item.id,
    });

    setSelectedRowId(item.id);
  };

  // Add this function to hide the context menu
  const hideContextMenu = () => {
    setSelectedRowId(null); // Reset selected row index
    setContextMenu({ visible: false, x: 0, y: 0, itemId: null });
  };

  const onDelete = async (row) => {
    try {
      const token = sessionStorage.getItem("jwt"); // Retrieve JWT token from localStorage
      if (pathname === "/admin/users") {
        const username = String(row.username).replace(/"/g, ''); // Remove quotes if they exist
        console.log("username that is deleting:", username);
        const response = await apiRequest(`/api/users/deleteUser?username=${username}`, 'POST', null, token)
      }
      else {
        const reservationCode = String(row.id).replace(/"/g, ''); // Remove quotes if they exist
        console.log("reservationCode:", reservationCode);
        const response = await apiRequest(`/api/reservations/deleteReservation?reservationCode=${reservationCode}`, 'POST', null, token)
      }

      fetchReservations();
      setSelectedRowId(null);
    } catch (error) {
      try {
        // Parse the error message
        const errorData = JSON.parse(error.message);
        if (errorData.Body && errorData.Body.error) {
          window.alert(errorData.Body.error); // Display the error message
        }
      } catch (parseError) {
        console.error("Failed to parse error message:", parseError);
      }
    }
  };

  useEffect(() => {
    const handleClick = () => {
      if (contextMenu.visible) {
        hideContextMenu();
      }
    };

    const handleScroll = () => {
      // Hide context menu when scrolling
      if (contextMenu.visible) {
        setContextMenu(prev => ({ ...prev, visible: false }));
      }
    };

    const parentContainer = mainBodyRef.current?.closest('.body');

    if (parentContainer) {
      parentContainer.addEventListener('scroll', handleScroll);
      window.addEventListener('scroll', handleScroll);
    }
    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
      if (parentContainer) {
        parentContainer.removeEventListener('scroll', handleScroll);
        window.removeEventListener('scroll', handleScroll);
      }
    };
  }, [contextMenu.visible]);

  // Helper function to format the date to "DD.MM.YYYY"
  const formatDate = (date) => {
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, '0'); // Add leading zero if day is a single digit
    const month = (d.getMonth() + 1).toString().padStart(2, '0'); // Get the month (zero-indexed), add leading zero if needed
    const year = d.getFullYear(); // Get the full year

    return `${day}.${month}.${year}`; // Return the formatted date
  };

  // Helper function to format the time to "HH:mm - HH:mm"
  const formatTime = (startDate, endDate) => {
    const formatTimeString = (date) => {
      const d = new Date(date);
      const hours = d.getHours().toString().padStart(2, '0'); // Get the hours and pad to 2 digits
      const minutes = d.getMinutes().toString().padStart(2, '0'); // Get the minutes and pad to 2 digits
      return `${hours}:${minutes}`;
    };

    const startTime = formatTimeString(startDate);
    const endTime = formatTimeString(endDate);

    return `${startTime} - ${endTime}`;
  };

  useEffect(() => {
    const handleResize = () => {
      // Force a re-render to update scroll status
      setForceUpdate(prev => !prev);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [forceUpdate, setForceUpdate] = useState(false);

  const needsScroll = (text, columnKey) => {
    if (!text) return false;
    const container = cellRefs.current[`${columnKey}`];
    return checkTextOverflow(text.toString(), container);
  };

  const handleRowClick = (row) => {
    if (pathname === "/admin/users") {
      // In users page, allow selection
      onUserSelect(row);
    }
  };

  const isRowSelected = (row) => {
    return selectedRowId === row.id || selectedUsers.has(row.id);
  };

  return (
    <div style={{ overflow: "auto" }} >
      <div ref={tableRef} className="table-container"  {...rest} >
        {/* Table Header */}
        <div className="table-header">
          {columns.map((col, index) => (
            <div
              key={index}
              className="header-cell"
              onClick={
                // Disable the function for "username" only on the "admin/history" page
                ((pathname.includes('history') && col.key === 'name') || col.key === 'seats')
                  ? undefined  // Do not run for "username" on the history page
                  : // Otherwise, run for all other columns except "Name", "password", and "email"
                  (col.key !== 'password' && col.key !== 'email')
                    ? () => sortData(col.key)
                    : undefined
              }
              style={{ cursor: ((pathname.includes('history') && col.key === 'name') || col.key === 'seats') ? 'default' : 'pointer' }}
            >
              <div className="columns-container" style={{ display: 'flex', alignItems: 'center', lineHeight: '1' }}>
                {/* Hide sort icons for "username" column on the "admin/history" page */}
                {!((pathname.includes('history') && col.key === 'name') || col.key === 'seats') &&
                  (col.key !== 'password' && col.key !== 'email') && (
                    <span className="sort-icons">
                      <span
                        className={`up-arrow mx-2 ${sortConfig.key === col.key && sortConfig.direction === 'ascending' ? 'active' : ''}`}
                        style={{ marginBottom: '1px' }}
                      >
                        ▲
                      </span>
                      <span
                        className={`down-arrow mx-2 ${sortConfig.key === col.key && sortConfig.direction === 'descending' ? 'active' : ''}`}
                        style={{ marginTop: '-2px' }}
                      >
                        ▼
                      </span>
                    </span>
                  )}
                <div>{col.title}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Table Body */}
        <div className="scrollbar" id="style-9" ref={mainBodyRef}>
          <div className="force-overflow">
            <div className="table-body" style={{ position: visibleRows <= 3 ? "unset" : "unset" }}>
              {sortedData.slice(0, visibleRows).map((row, rowIndex) => (
                <div
                  key={rowIndex}
                  className={`row m-0 default-row ${isRowSelected(row) ? 'selected-row' : ''}`}
                  style={{ position: "relative" }}
                  onClick={() => handleRowClick(row)}
                  onContextMenu={(e) => handleContextMenu(row, e)}
                  onTouchStart={(e) => handleTouchStart(row, e)}
                  onTouchEnd={handleTouchEnd}
                  onTouchMove={handleTouchMove}
                  onTouchCancel={handleTouchEnd}
                >
                  {columns.map((col, colIndex) => (
                    <div
                      key={`${rowIndex}-${col.key}`}
                      className={`cell ${needsScroll(row[col.key], `${rowIndex}-${col.key}`) ? 'needs-scroll' : ''}`}
                      ref={el => cellRefs.current[`${rowIndex}-${col.key}`] = el}
                    >
                      {col.key === 'startDate' ? (
                        <>
                          {formatDate(row.startDate)}
                          <br />
                          {formatTime(row.startDate, row.endDate)}
                        </>
                      ) : (
                        <div className="scroll-container">
                          <div className="scroll-text">
                            {row[col.key]}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))}

              {contextMenu.visible && (
                <div
                  className="dropdown-container"
                  style={{
                    position: 'fixed',
                    left: `${contextMenu.x}px`,
                    top: `${contextMenu.y}px`,
                    transform: 'translateX(-50%)', // Center horizontally
                  }}
                >
                  <div
                    className="dropdown-button"
                    onClick={() => {
                      const row = sortedData.find(i => i.id === contextMenu.itemId);
                      console.log("row that is being edited:", row);
                      if (row) onEdit(row);
                      hideContextMenu();
                    }}
                  >
                    {selectedLanguageRef.current === 'EN' ? "Edit" : "Izmeni"}
                  </div>

                  <div
                    className="mx-3"
                    style={{
                      borderTop: 'solid 1px #FFF'
                    }}
                  />

                  <div
                    className="dropdown-button"
                    onClick={() => {
                      const row = sortedData.find(i => i.id === contextMenu.itemId);
                      if (row) onDelete(row);
                      hideContextMenu();
                    }}
                  >
                    {selectedLanguageRef.current === 'EN' ? "Delete" : "Ukloni"}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataTable;